import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Alert, Button, ButtonGroup, Card, Nav, Spinner, Tab } from 'react-bootstrap';
import {
  adminService,
  MostPlayedQuizReportItem,
  UserSignupsReportResponse,
  UserSignupsReportSeries,
} from '../services/api';
import './AdminReports.css';

const TOP_N = 15;

type SignupsPeriodKey = '24h' | '7d' | '30d';

const SIGNUPS_PERIODS: { key: SignupsPeriodKey; label: string }[] = [
  { key: '24h', label: 'Últimas 24h' },
  { key: '7d', label: 'Últimos 7 dias' },
  { key: '30d', label: 'Últimos 30 dias' },
];

const AdminReports: React.FC = () => {
  const [activeKey, setActiveKey] = useState('most-played');

  // Most-played-quizzes tab
  const [items, setItems] = useState<MostPlayedQuizReportItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Signups tab
  const [signups, setSignups] = useState<UserSignupsReportResponse | null>(null);
  const [signupsLoading, setSignupsLoading] = useState(false);
  const [signupsError, setSignupsError] = useState<string | null>(null);
  const [signupsPeriod, setSignupsPeriod] = useState<SignupsPeriodKey>('24h');

  const loadMostPlayed = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminService.getMostPlayedQuizzes(TOP_N);
      setItems(res.items ?? []);
    } catch {
      setError('Não foi possível carregar o relatório.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSignups = useCallback(async () => {
    setSignupsLoading(true);
    setSignupsError(null);
    try {
      const res = await adminService.getUserSignupsReport();
      setSignups(res);
    } catch {
      setSignupsError('Não foi possível carregar o relatório de cadastros.');
      setSignups(null);
    } finally {
      setSignupsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeKey === 'most-played') {
      void loadMostPlayed();
    } else if (activeKey === 'signups') {
      void loadSignups();
    }
  }, [activeKey, loadMostPlayed, loadSignups]);

  const maxCount = items.length ? Math.max(...items.map((i) => i.playCount), 1) : 1;

  const activeSignupsSeries: UserSignupsReportSeries | null = useMemo(() => {
    if (!signups) return null;
    if (signupsPeriod === '24h') return signups.last24Hours;
    if (signupsPeriod === '7d') return signups.last7Days;
    return signups.last30Days;
  }, [signups, signupsPeriod]);

  return (
    <>
      <h3>Relatórios</h3>
      <p className="text-muted mb-4">
        Partidas e cadastros de contas administrativas não entram nas contagens abaixo.
      </p>

      <Tab.Container activeKey={activeKey} onSelect={(k) => k && setActiveKey(k)}>
        <Nav variant="tabs" className="admin-reports-subnav mb-3">
          <Nav.Item>
            <Nav.Link eventKey="most-played">Quizzes mais jogados</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="signups">Cadastros</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="other">Outros</Nav.Link>
          </Nav.Item>
        </Nav>
        <Tab.Content>
          <Tab.Pane eventKey="most-played">
            <Card className="admin-report-card">
              <Card.Body>
                <Card.Title as="h5" className="mb-1">
                  Partidas concluídas (solo)
                </Card.Title>
                <Card.Subtitle className="text-muted small mb-4">
                  Top {TOP_N} por tentativas concluídas (usuários que não são administradores).
                  {' '}
                  Esse relatório inclui uma linha agregada &quot;Partida rápida&quot;: cada sessão desse modo concluída conta como uma jogada.
                  {' '}
                  Rascunhos e quizzes desativados não entram nas contagens dos quizzes normais.
                </Card.Subtitle>

                {error && <Alert variant="danger">{error}</Alert>}

                {loading && (
                  <div className="text-center py-5">
                    <Spinner animation="border" role="status" variant="primary" />
                  </div>
                )}

                {!loading && !error && items.length === 0 && (
                  <p className="text-muted text-center py-4 mb-0">
                    Ainda não há partidas concluídas por jogadores não administradores.
                  </p>
                )}

                {!loading && items.length > 0 && (
                  <div className="admin-most-played-chart">
                    {items.map((row, idx) => {
                      const themeLine = row.theme?.trim() ?? '';
                      const fullLabel =
                        themeLine.length > 0 ? `${row.title} — ${themeLine}` : row.title;
                      return (
                      <div key={row.quizId} className="admin-most-played-row">
                        <div className="admin-most-played-label" title={fullLabel}>
                          <span className="admin-most-played-rank">{idx + 1}.</span>
                          <span className="admin-most-played-title-stack">
                            <span className="admin-most-played-title">{row.title}</span>
                            {themeLine.length > 0 ? (
                              <span className="admin-most-played-theme">{themeLine}</span>
                            ) : null}
                          </span>
                        </div>
                        <div className="admin-most-played-bar-wrap">
                          <div
                            className="admin-most-played-bar"
                            style={{ width: `${(row.playCount / maxCount) * 100}%` }}
                          />
                        </div>
                        <div className="admin-most-played-count">{row.playCount}</div>
                      </div>
                    );
                    })}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab.Pane>

          <Tab.Pane eventKey="signups">
            <Card className="admin-report-card">
              <Card.Body>
                <Card.Title as="h5" className="mb-1">
                  Novos cadastros
                </Card.Title>
                <Card.Subtitle className="text-muted small mb-3">
                  Conta apenas usuários cadastrados (exclui contas administrativas). Janelas de
                  tempo são calculadas em UTC; rótulos são exibidos no fuso horário do navegador.
                </Card.Subtitle>

                <ButtonGroup size="sm" className="admin-signups-period-toggle mb-3">
                  {SIGNUPS_PERIODS.map((p) => (
                    <Button
                      key={p.key}
                      variant={signupsPeriod === p.key ? 'primary' : 'outline-primary'}
                      onClick={() => setSignupsPeriod(p.key)}
                      type="button"
                    >
                      {p.label}
                    </Button>
                  ))}
                </ButtonGroup>

                {signupsError && <Alert variant="danger">{signupsError}</Alert>}

                {signupsLoading && (
                  <div className="text-center py-5">
                    <Spinner animation="border" role="status" variant="primary" />
                  </div>
                )}

                {!signupsLoading && !signupsError && activeSignupsSeries && (
                  <SignupsBarChart series={activeSignupsSeries} />
                )}
              </Card.Body>
            </Card>
          </Tab.Pane>

          <Tab.Pane eventKey="other">
            <Card className="admin-report-card">
              <Card.Body>
                <p className="text-muted mb-0">Nenhum outro relatório disponível no momento.</p>
              </Card.Body>
            </Card>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </>
  );
};

interface SignupsBarChartProps {
  series: UserSignupsReportSeries;
}

const SignupsBarChart: React.FC<SignupsBarChartProps> = ({ series }) => {
  const isHourly = series.granularity === 'hour';

  // Format each bucket label in the user's locale. UTC ISO strings parse correctly via Date().
  const hourFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        hour: '2-digit',
        minute: '2-digit',
      }),
    []
  );
  const dayFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        day: '2-digit',
        month: '2-digit',
      }),
    []
  );
  const tooltipFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        dateStyle: 'short',
        timeStyle: isHourly ? 'short' : undefined,
      }),
    [isHourly]
  );

  const max = Math.max(1, ...series.buckets.map((b) => b.count));

  if (series.buckets.length === 0) {
    return (
      <p className="text-muted text-center py-4 mb-0">
        Nenhum cadastro nesse período.
      </p>
    );
  }

  return (
    <div className="admin-signups-chart">
      <div className="admin-signups-chart-meta">
        <span>
          Total no período:&nbsp;
          <strong>{series.total}</strong>
        </span>
      </div>
      <div
        className={`admin-signups-bars admin-signups-bars--${isHourly ? 'hourly' : 'daily'}`}
        role="list"
      >
        {series.buckets.map((bucket) => {
          const date = new Date(bucket.bucketStartUtc);
          const label = isHourly ? hourFormatter.format(date) : dayFormatter.format(date);
          const tooltip = `${tooltipFormatter.format(date)} · ${bucket.count} cadastro${
            bucket.count === 1 ? '' : 's'
          }`;
          const heightPct = (bucket.count / max) * 100;
          return (
            <div
              key={bucket.bucketStartUtc}
              className="admin-signups-bar-cell"
              role="listitem"
              title={tooltip}
            >
              <div className="admin-signups-bar-track">
                <div
                  className="admin-signups-bar-fill"
                  style={{ height: `${heightPct}%` }}
                  aria-label={tooltip}
                >
                  {bucket.count > 0 && (
                    <span className="admin-signups-bar-count">{bucket.count}</span>
                  )}
                </div>
              </div>
              <span className="admin-signups-bar-label">{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminReports;
